/**
 * Barra de pestañas de Staff (006 · T016, research R-2; design-system §8.1).
 *
 * Tres enlaces —Inicio (`/`), Asistencia (`/asistencia`) y Perfil (`/perfil`)— con icono Lucide de
 * 24 px y etiqueta de 12 px. No es una ruta de layout: cada pantalla con pestañas la pasa al slot
 * `nav` de `Screen` (`nav={<TabBar active="home" />}`); las demás (marcación, activación, estados
 * especiales) simplemente no la pasan.
 *
 * - `<nav aria-label="Navegación principal">`; la pestaña activa lleva `aria-current="page"`.
 * - Activa en Coral 700 (`text-fm-primary-compact`) **y** una barra superior de 2 px: el estado
 *   nunca se comunica solo con color (§10). Inactivas en Neutro 600 (`text-fm-text-secondary`).
 * - Alto 56 px + `env(safe-area-inset-bottom)` (`fm-safe-bottom`); cada enlace ≥ 44 × 44 px.
 */
import { Link } from '@tanstack/react-router';
import { CalendarDays, CircleUser, House, type LucideIcon } from 'lucide-react';

import { t } from '../../i18n';

import { cn } from './cn';

export type TabId = 'home' | 'history' | 'profile';

/** Rutas de las pestañas (contracts/staff-ui.md §2). */
export type TabPath = '/' | '/asistencia' | '/perfil';

interface TabDefinition {
  id: TabId;
  to: TabPath;
  icon: LucideIcon;
  testId: string;
}

export const TABS: readonly TabDefinition[] = [
  { id: 'home', to: '/', icon: House, testId: 'tab-home' },
  { id: 'history', to: '/asistencia', icon: CalendarDays, testId: 'tab-history' },
  { id: 'profile', to: '/perfil', icon: CircleUser, testId: 'tab-profile' },
];

export interface TabBarProps {
  active: TabId;
  className?: string;
}

export function TabBar({ active, className }: TabBarProps) {
  return (
    <nav
      aria-label={t('shell.tabs.label')}
      data-testid="tab-bar"
      className={cn('fm-safe-bottom border-t border-fm-border bg-fm-surface', className)}
    >
      <ul className="flex h-14 items-stretch justify-around px-fm-2">
        {TABS.map((tab) => {
          const isActive = tab.id === active;
          const Icon = tab.icon;
          return (
            <li key={tab.id} className="flex flex-1 justify-center">
              <Link
                to={tab.to}
                data-testid={tab.testId}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'relative flex min-h-11 min-w-11 flex-1 flex-col items-center justify-center gap-0.5',
                  'rounded-fm-sm text-fm-xs font-ui font-semibold',
                  'transition-colors duration-fm-fast ease-fm-standard motion-reduce:transition-none',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
                  isActive ? 'text-fm-primary-compact' : 'text-fm-text-secondary',
                )}
              >
                {isActive ? (
                  <span
                    aria-hidden="true"
                    data-tab-indicator=""
                    className="absolute top-0 h-0.5 w-8 rounded-fm-pill bg-fm-primary-compact"
                  />
                ) : null}
                <Icon aria-hidden="true" size={24} strokeWidth={isActive ? 2.25 : 2} />
                <span>{t(`shell.tabs.${tab.id}`)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
